import { useState, useEffect } from "react";
import { Link, useNavigate } from "@/lib/router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { graphsApi, type AgentGraph, type GraphDefinition } from "../api/graphs";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { WorkflowTemplateGallery } from "../components/workflow-studio/WorkflowTemplates";
import { Button } from "@/components/ui/button";
import { EmptyState } from "../components/EmptyState";
import { PageSkeleton } from "../components/PageSkeleton";
import { timeAgo } from "../lib/timeAgo";
import { cn } from "../lib/utils";
import {
  GitBranch,
  Plus,
  Trash2,
  Workflow,
  Bot,
  GitFork,
  Hand,
  Clock,
  Database,
  Shield,
  Globe,
  CreditCard,
  Mail,
} from "lucide-react";

/** Count specific node types in a graph definition for the summary */
function nodeSummary(def: GraphDefinition | null | undefined) {
  if (!def?.nodes) return { total: 0, agents: 0, decisions: 0, hitl: 0 };
  return {
    total: def.nodes.length,
    agents: def.nodes.filter((n) => n.type === "agent_ref").length,
    decisions: def.nodes.filter((n) => n.type === "decision").length,
    hitl: def.nodes.filter((n) => n.type === "human_review").length,
  };
}

export function WorkflowStudio() {
  const { selectedPracticeId } = usePractice();
  const { setBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showTemplates, setShowTemplates] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    setBreadcrumbs([{ label: "Workflow Studio" }]);
  }, [setBreadcrumbs]);

  const { data: workflows, isLoading } = useQuery({
    queryKey: ["workflow-graphs", selectedPracticeId],
    queryFn: () => graphsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const createMutation = useMutation({
    mutationFn: (args: { name: string; definition: GraphDefinition }) =>
      graphsApi.create(selectedPracticeId!, {
        name: args.name,
        description: null as any,
        graphDefinition: args.definition,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["workflow-graphs", selectedPracticeId] });
      navigate(`/workflows/${created.id}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => graphsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow-graphs", selectedPracticeId] });
      setDeleteId(null);
    },
  });

  function handleTemplateSelect(name: string, definition: GraphDefinition) {
    setShowTemplates(false);
    createMutation.mutate({ name, definition });
  }

  if (!selectedPracticeId) {
    return <EmptyState icon={GitBranch} message="Select a practice to view workflows." />;
  }

  if (isLoading) return <PageSkeleton variant="list" />;

  // Filter to only show workflow graphs (those with wf_start nodes, not Agent Studio graphs)
  // By default, hide archived graphs unless showArchived is toggled on
  const graphs = (workflows ?? []).filter((wf) => {
    const nodes = wf.graphDefinition?.nodes;
    if (!nodes || nodes.length === 0) return false;
    if (!nodes.some((n) => n.type === "wf_start")) return false;
    if (!showArchived && wf.status === "archived") return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Workflow Studio</h1>
          <p className="text-sm text-muted-foreground">
            Build deterministic healthcare processes. Drop in agents from Agent Studio as needed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowArchived((v) => !v)}
            className={cn(
              "text-xs px-2.5 py-1.5 rounded-md border transition-colors",
              showArchived
                ? "border-muted-foreground/40 bg-muted text-muted-foreground"
                : "border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/40",
            )}
          >
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
          <Button onClick={() => setShowTemplates(true)} disabled={createMutation.isPending}>
            <Plus className="h-4 w-4 mr-1.5" />
            New Workflow
          </Button>
        </div>
      </div>

      {/* Workflow cards grid */}
      {graphs.length === 0 ? (
        <EmptyState
          icon={GitBranch}
          message="No workflows yet. Create one from a template or start from scratch."
          action="New Workflow"
          onAction={() => setShowTemplates(true)}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {graphs.map((wf) => {
            const summary = nodeSummary(wf.graphDefinition);
            return (
              <div key={wf.id} className="group relative border border-border rounded-lg hover:border-primary/30 hover:shadow-md transition-all">
                <Link
                  to={`/workflows/${wf.id}`}
                  className="block p-5 no-underline text-inherit"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <GitBranch className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold truncate">{wf.name}</h3>
                      {wf.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{wf.description}</p>
                      )}
                    </div>
                    <span className={cn(
                      "text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0",
                      wf.status === "deployed"
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                        : "bg-muted text-muted-foreground",
                    )}>
                      {wf.status}
                    </span>
                  </div>

                  {/* Node summary badges */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 rounded-full px-2 py-0.5">
                      <Workflow className="h-2.5 w-2.5" /> {summary.total} steps
                    </span>
                    {summary.agents > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-violet-600 dark:text-violet-400 bg-violet-100/50 dark:bg-violet-900/20 rounded-full px-2 py-0.5">
                        <Bot className="h-2.5 w-2.5" /> {summary.agents} agents
                      </span>
                    )}
                    {summary.decisions > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 bg-amber-100/50 dark:bg-amber-900/20 rounded-full px-2 py-0.5">
                        <GitFork className="h-2.5 w-2.5" /> {summary.decisions} decisions
                      </span>
                    )}
                    {summary.hitl > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-rose-600 dark:text-rose-400 bg-rose-100/50 dark:bg-rose-900/20 rounded-full px-2 py-0.5">
                        <Hand className="h-2.5 w-2.5" /> {summary.hitl} HITL
                      </span>
                    )}
                  </div>

                  <p className="text-[11px] text-muted-foreground">
                    Modified {timeAgo(wf.updatedAt)}
                  </p>
                </Link>

                {/* Delete button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDeleteId(wf.id);
                  }}
                  className="absolute top-3 right-3 p-1.5 rounded-md opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Template gallery modal */}
      {showTemplates && (
        <WorkflowTemplateGallery
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {/* Delete confirmation */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-xl border border-border bg-card shadow-xl mx-4 p-6">
            <h3 className="text-sm font-semibold mb-2">Delete Workflow?</h3>
            <p className="text-sm text-muted-foreground mb-4">This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button variant="destructive" size="sm" onClick={() => deleteMutation.mutate(deleteId)} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
